import { Box, Button, Chip, TextField, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import type { ChartOptions } from 'chart.js';
import './chartConfig';

import { METRICS, SONAR_SERIES_COLORS } from '../constants';
import { MetricKey, TimeSeriesPoint } from '../types';
import {
  filterByDateRange,
  formatDate,
  formatMetricValue,
  getDateRangeLabel,
  getLatestValue,
  ratingLabel,
  toNumber,
} from '../utils';

const DEFAULT_TOP_SERIES_COUNT = 3;
const SECONDARY_TOP_SERIES_COUNT = 6;
const AUTO_SELECT_ALL_LIMIT = 8;
const TOOLTIP_VISIBLE_ITEMS_LIMIT = 10;
const LARGE_RANGE_HINT_RATIO = 100;

const truncateLabel = (label: string, max = 34) =>
  label.length > max ? `${label.slice(0, max - 1)}...` : label;

const isRatingMetric = (metric: MetricKey) =>
  metric === 'reliability_rating' ||
  metric === 'security_rating' ||
  metric === 'sqale_rating';

const formatMetricValueWithGrade = (value: number | null, metric: MetricKey) => {
  const formattedValue = formatMetricValue(value, metric);
  if (value === null || !isRatingMetric(metric)) {
    return formattedValue;
  }

  return `${formattedValue} (${ratingLabel(value)})`;
};

const getDefaultGroups = (availableGroups: string[]) =>
  availableGroups.length <= AUTO_SELECT_ALL_LIMIT
    ? availableGroups
    : availableGroups.slice(0, DEFAULT_TOP_SERIES_COUNT);

const matchesSelection = (selectedGroups: string[], candidateGroups: string[]) => {
  if (selectedGroups.length !== candidateGroups.length) {
    return false;
  }

  const selectedSet = new Set(selectedGroups);
  return candidateGroups.every(group => selectedSet.has(group));
};

const useStyles = makeStyles({
  chipScroller: {
    gap: 8,
    maxHeight: 92,
    overflowY: 'auto',
    paddingRight: 4,
    scrollbarWidth: 'thin',
    scrollbarColor: '#cbd5e1 #f1f5f9',
    '&::-webkit-scrollbar': {
      width: 10,
      height: 10,
      backgroundColor: '#f1f5f9',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#f1f5f9',
      borderRadius: 8,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#cbd5e1',
      borderRadius: 8,
      border: '2px solid #f1f5f9',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: '#94a3b8',
    },
    '&::-webkit-scrollbar-corner': {
      backgroundColor: '#f1f5f9',
    },
  },
});

export const SonarTimeSeriesChart = ({
  data,
  metric,
  fromDate,
  toDate,
}: {
  data: TimeSeriesPoint[];
  metric: MetricKey;
  fromDate: string;
  toDate: string;
}) => {
  const classes = useStyles();
  const filtered = filterByDateRange(data, fromDate, toDate);
  const dates = [...new Set(filtered.map(point => point.time))].sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime(),
  );
  const groupNames = [...new Set(filtered.map(point => point.metric))].filter(Boolean);

  const rankedGroups = groupNames
    .map(group => ({ group, latest: getLatestValue(filtered.filter(point => point.metric === group)) }))
    .sort((a, b) => {
      const aValue = a.latest ?? (METRICS[metric].better === 'higher' ? -Infinity : Infinity);
      const bValue = b.latest ?? (METRICS[metric].better === 'higher' ? -Infinity : Infinity);
      return METRICS[metric].better === 'higher' ? bValue - aValue : aValue - bValue;
    });

  const availableGroups = rankedGroups.map(item => item.group);
  const defaultGroups = getDefaultGroups(availableGroups);
  const [selectedGroups, setSelectedGroups] = useState<string[]>(defaultGroups);
  const [searchQuery, setSearchQuery] = useState('');
  const [yScaleMode, setYScaleMode] = useState<'linear' | 'logarithmic'>('linear');

  useEffect(() => {
    setSelectedGroups(defaultGroups);
    setSearchQuery('');
    setYScaleMode('linear');
  }, [data, metric, fromDate, toDate]);

  const visibleGroups = selectedGroups.filter(group => availableGroups.includes(group));
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const filteredGroups = availableGroups.filter(group =>
    !normalizedSearchQuery || group.toLowerCase().includes(normalizedSearchQuery),
  );
  const allSelected = matchesSelection(visibleGroups, availableGroups);
  const top3Selected = matchesSelection(
    visibleGroups,
    availableGroups.slice(0, DEFAULT_TOP_SERIES_COUNT),
  );
  const top6Selected = matchesSelection(
    visibleGroups,
    availableGroups.slice(0, SECONDARY_TOP_SERIES_COUNT),
  );
  const clearSelected = visibleGroups.length === 0;
  const usePointOnlyHover = visibleGroups.length > SECONDARY_TOP_SERIES_COUNT - 1;

  const presetButtonStyle = (active: boolean) => ({
    borderRadius: 999,
    textTransform: 'none' as const,
    fontWeight: 800,
    background: active ? '#0f172a' : '#ffffff',
    color: active ? '#ffffff' : '#0f172a',
    borderColor: active ? '#0f172a' : '#cbd5e1',
    boxShadow: active ? '0 10px 24px rgba(15, 23, 42, 0.16)' : 'none',
  });

  const toggleGroup = (group: string) => {
    setSelectedGroups(current => {
      const safeCurrent = current.filter(item => availableGroups.includes(item));
      if (safeCurrent.includes(group)) {
        return safeCurrent.filter(item => item !== group);
      }
      return [...safeCurrent, group];
    });
  };

  const colorForGroup = (group: string) => {
    const index = Math.max(0, availableGroups.indexOf(group));
    return SONAR_SERIES_COLORS[index % SONAR_SERIES_COLORS.length];
  };

  const rawValues = filtered
    .filter(point => visibleGroups.includes(point.metric))
    .map(point => toNumber(point.value))
    .filter((value): value is number => value !== null);
  const positiveRawValues = rawValues.filter(value => value > 0);
  const hasPositiveValues = positiveRawValues.length > 0;

  const dataMin = rawValues.length ? Math.min(...rawValues) : 0;
  const dataMax = rawValues.length ? Math.max(...rawValues) : 1;
  const minPositive = hasPositiveValues ? Math.min(...positiveRawValues) : null;
  const spreadRatio = hasPositiveValues && minPositive !== null
    ? dataMax / minPositive
    : 1;
  const hasLargeRange = spreadRatio >= LARGE_RANGE_HINT_RATIO;
  const showLargeRangeHint = yScaleMode === 'linear' && hasLargeRange;
  const dataSpread = Math.max(dataMax - dataMin, 0.01);
  const axisPadding = dataSpread * 0.35;
  const linearYMax = METRICS[metric].max
    ? Math.min(METRICS[metric].max!, dataMax + axisPadding)
    : Math.max(dataMax + axisPadding, 1);
  const linearYMin = isRatingMetric(metric)
    ? Math.max(1, dataMin - axisPadding)
    : Math.max(0, dataMin - axisPadding);
  const logYMin = hasPositiveValues
    ? Math.max(minPositive as number, Number.EPSILON)
    : Number.EPSILON;
  const logYMax = hasPositiveValues
    ? Math.max(dataMax, logYMin * 10)
    : 1;
  const usingLogScale = yScaleMode === 'logarithmic';
  const yMin = usingLogScale ? logYMin : linearYMin;
  const yMax = usingLogScale ? logYMax : linearYMax;

  useEffect(() => {
    if (!hasLargeRange && yScaleMode !== 'linear') {
      setYScaleMode('linear');
    }
  }, [hasLargeRange, yScaleMode]);

  const chartData = useMemo(() => ({
    labels: dates.map(d => formatDate(d)),
    datasets: visibleGroups.map((group, i) => {
      const color = colorForGroup(group);
      const byDate = new Map(
        filtered.filter(p => p.metric === group).map(p => [p.time, toNumber(p.value)]),
      );
      const sanitize = (v: number | null) =>
        (isRatingMetric(metric) && v === 0) || (usingLogScale && v !== null && v <= 0)
          ? null
          : v;
      return {
        label: group,
        data: dates.map(d => sanitize(byDate.get(d) ?? null)),
        borderColor: color,
        backgroundColor: color + (i === 0 ? '22' : '0d'),
        borderWidth: i === 0 ? 3 : 2,
        pointRadius: 4,
        pointHoverRadius: 7,
        pointHitRadius: usePointOnlyHover ? 18 : 8,
        clip: false as const,
        tension: 0.35,
        fill: false,
        spanGaps: false,
      };
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [visibleGroups.join(','), dates.join(','), filtered, usePointOnlyHover, usingLogScale]);

  const chartOptions = useMemo((): ChartOptions<'line'> => {
    const hideState = { timer: null as number | null };

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 400 },
      interaction: usePointOnlyHover
        ? { mode: 'nearest', intersect: true }
        : { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        datalabels: { display: false },
        tooltip: {
          enabled: false,
          external: context => {
            const { chart, tooltip } = context;
            const parent = chart.canvas.parentNode as HTMLElement | null;
            if (!parent) return;

            if (window.getComputedStyle(parent).position === 'static') {
              parent.style.position = 'relative';
            }

            let tooltipEl = parent.querySelector<HTMLDivElement>('.sonar-chart-tooltip');
            if (!tooltipEl) {
              tooltipEl = document.createElement('div');
              tooltipEl.className = 'sonar-chart-tooltip';
              tooltipEl.style.position = 'absolute';
              tooltipEl.style.background = '#0f172a';
              tooltipEl.style.color = '#ffffff';
              tooltipEl.style.borderRadius = '10px';
              tooltipEl.style.padding = '10px 12px';
              tooltipEl.style.pointerEvents = 'none';
              tooltipEl.style.fontSize = '13px';
              tooltipEl.style.boxShadow = '0 12px 24px rgba(2, 6, 23, 0.35)';
              tooltipEl.style.zIndex = '20';
              tooltipEl.style.opacity = '0';
              tooltipEl.style.maxWidth = '420px';
              parent.appendChild(tooltipEl);
            }

            if (hideState.timer !== null) {
              window.clearTimeout(hideState.timer);
              hideState.timer = null;
            }

            if (tooltip.opacity === 0) {
              hideState.timer = window.setTimeout(() => {
                if (tooltipEl) {
                  tooltipEl.style.opacity = '0';
                }
              }, 120);
              return;
            }

            const points = (tooltip.dataPoints || []).filter(point =>
              toNumber(point.parsed.y) !== null,
            );

            if (!points.length) {
              tooltipEl.style.opacity = '0';
              return;
            }

            tooltipEl.replaceChildren();

            const title = document.createElement('div');
            title.style.fontWeight = 'bold';
            title.style.marginBottom = '6px';
            title.textContent = tooltip.title?.[0] || '';
            tooltipEl.appendChild(title);

            const body = document.createElement('div');
            body.style.overflowY = 'hidden';

            const visiblePoints = points.slice(0, TOOLTIP_VISIBLE_ITEMS_LIMIT);
            const hiddenCount = Math.max(0, points.length - visiblePoints.length);

            for (const point of visiblePoints) {
              const row = document.createElement('div');
              row.style.display = 'flex';
              row.style.alignItems = 'center';
              row.style.gap = '6px';
              row.style.marginBottom = '2px';

              const colorSwatch = document.createElement('span');
              colorSwatch.style.width = '10px';
              colorSwatch.style.height = '10px';
              colorSwatch.style.borderRadius = '2px';
              colorSwatch.style.flex = '0 0 auto';
              colorSwatch.style.background = String(point.dataset.borderColor || '#93c5fd');

              const text = document.createElement('span');
              text.textContent = `${truncateLabel(String(point.dataset.label || '-'))}: ${formatMetricValueWithGrade(point.parsed.y, metric)}`;

              row.appendChild(colorSwatch);
              row.appendChild(text);
              body.appendChild(row);
            }

            if (hiddenCount > 0) {
              const moreRow = document.createElement('div');
              moreRow.style.marginTop = '4px';
              moreRow.style.fontWeight = '700';
              moreRow.style.color = '#cbd5e1';
              moreRow.textContent = `+${hiddenCount} more`;
              body.appendChild(moreRow);
            }

            tooltipEl.appendChild(body);

            const canvasRect = chart.canvas.getBoundingClientRect();
            const parentRect = parent.getBoundingClientRect();

            let left = canvasRect.left - parentRect.left + tooltip.caretX + 12;
            let top = canvasRect.top - parentRect.top + tooltip.caretY + 12;

            const maxLeft = Math.max(8, parent.clientWidth - tooltipEl.offsetWidth - 8);
            const maxTop = Math.max(8, parent.clientHeight - tooltipEl.offsetHeight - 8);

            left = Math.min(Math.max(8, left), maxLeft);
            top = Math.min(Math.max(8, top), maxTop);

            tooltipEl.style.left = `${left}px`;
            tooltipEl.style.top = `${top}px`;
            tooltipEl.style.opacity = '1';
          },
        },
      },
      onClick: (_event, elements, chart) => {
        if (!elements.length) {
          return;
        }

        const activeTooltipPoints = (chart.tooltip?.dataPoints || []).filter(point =>
          toNumber(point.parsed.y) !== null,
        );

        // Allow point-click unselect only when the tooltip is showing a single entry.
        if (activeTooltipPoints.length !== 1) {
          return;
        }

        const firstElement = elements[0];
        if (firstElement.datasetIndex === undefined) {
          return;
        }

        const dataset = chart.data.datasets[firstElement.datasetIndex];
        const groupLabel = typeof dataset?.label === 'string' ? dataset.label : '';
        if (!groupLabel) {
          return;
        }

        setSelectedGroups(current => current.filter(group => group !== groupLabel));
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 11 }, color: '#64748b', maxRotation: 35 },
        },
        y: {
          type: yScaleMode,
          min: yMin,
          max: yMax,
          grid: { color: '#e2e8f0' },
          ticks: {
            font: { size: 11 },
            color: '#64748b',
            callback: value => {
              const num = Number(value);
              if (metric === 'coverage') {
                return `${num.toFixed(1)}%`;
              }
              if (isRatingMetric(metric)) {
                if (num === 0) return '-';
                return `${num.toFixed(1)} (${ratingLabel(num)})`;
              }
              // Display exact values with thousands separator
              return num.toLocaleString('en-US', { maximumFractionDigits: num % 1 === 0 ? 0 : 1 });
            },
          },
        },
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, metric, yMax, yMin, usePointOnlyHover, yScaleMode]);

  if (!dates.length || !availableGroups.length) {
    return (
      <Box minHeight={420} display="flex" alignItems="center" justifyContent="center" flexDirection="column" style={{ color: '#64748b' }}>
        <Typography style={{ fontWeight: 900, marginBottom: 6 }}>No trend data available</Typography>
        <Typography variant="body2">Try a different classification, metric, group, or date range.</Typography>
      </Box>
    );
  }

  return (
    <Box style={{ maxWidth: '100%', overflowX: 'hidden', overflowY: 'visible' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" mb={2} style={{ gap: 10 }}>
        <Box>
          <Typography style={{ color: '#0f172a', fontWeight: 900, fontSize: 18 }}>
            {METRICS[metric].label} over time
          </Typography>
          <Typography style={{ color: '#64748b', fontSize: 13 }}>
            Showing {visibleGroups.length} selected series. Range: {getDateRangeLabel(filtered)}
          </Typography>
          {showLargeRangeHint && (
            <Typography style={{ color: '#92400e', fontSize: 12, marginTop: 4, fontWeight: 700 }}>
              Large value spread detected. Switch to Log scale to improve readability.
            </Typography>
          )}
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          {hasLargeRange && (
            <>
              <Button
                size="small"
                variant={yScaleMode === 'linear' ? 'contained' : 'outlined'}
                aria-pressed={yScaleMode === 'linear'}
                onClick={() => setYScaleMode('linear')}
                style={presetButtonStyle(yScaleMode === 'linear')}
              >
                Linear
              </Button>
              <Button
                size="small"
                variant={yScaleMode === 'logarithmic' ? 'contained' : 'outlined'}
                aria-pressed={yScaleMode === 'logarithmic'}
                onClick={() => setYScaleMode('logarithmic')}
                disabled={!hasPositiveValues}
                style={{
                  ...presetButtonStyle(yScaleMode === 'logarithmic'),
                  opacity: hasPositiveValues ? 1 : 0.6,
                }}
              >
                Log
              </Button>
            </>
          )}
          <Chip label={`${dates.length} date points`} style={{ background: '#eff6ff', color: '#1d4ed8', fontWeight: 800 }} />
        </Box>
      </Box>

      <Box mb={2} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 16, padding: 12 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 10, marginBottom: 10 }}>
          <Box>
            <Typography style={{ color: '#0f172a', fontWeight: 900, fontSize: 14 }}>Focus series</Typography>
            <Typography style={{ color: '#64748b', fontSize: 12 }}>Click a chip to show or hide it from the graph.</Typography>
          </Box>
          <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
            <Button
              size="small"
              variant={allSelected ? 'contained' : 'outlined'}
              aria-pressed={allSelected}
              onClick={() => setSelectedGroups(availableGroups)}
              style={presetButtonStyle(allSelected)}
            >
              All
            </Button>
            <Button
              size="small"
              variant={top3Selected ? 'contained' : 'outlined'}
              aria-pressed={top3Selected}
              onClick={() => setSelectedGroups(availableGroups.slice(0, DEFAULT_TOP_SERIES_COUNT))}
              style={presetButtonStyle(top3Selected)}
            >
              Top 3
            </Button>
            <Button
              size="small"
              variant={top6Selected ? 'contained' : 'outlined'}
              aria-pressed={top6Selected}
              onClick={() => setSelectedGroups(availableGroups.slice(0, SECONDARY_TOP_SERIES_COUNT))}
              style={presetButtonStyle(top6Selected)}
            >
              Top 6
            </Button>
            <Button
              size="small"
              variant={clearSelected ? 'contained' : 'outlined'}
              aria-pressed={clearSelected}
              onClick={() => setSelectedGroups([])}
              style={presetButtonStyle(clearSelected)}
            >
              Clear
            </Button>
          </Box>
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 10, marginBottom: 10 }}>
          <TextField
            aria-label="Search series"
            label="Search series"
            placeholder="Find a repository, team, or tribe"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            style={{ minWidth: 260, background: '#ffffff', borderRadius: 12 }}
          />
          <Typography style={{ color: '#64748b', fontSize: 12, fontWeight: 700 }}>
            Showing {filteredGroups.length} of {availableGroups.length} series
          </Typography>
        </Box>

        <Box
          display="flex"
          flexWrap="wrap"
          className={classes.chipScroller}
        >
          {filteredGroups.length ? (
            filteredGroups.map(group => {
              const active = visibleGroups.includes(group);
              const latest = rankedGroups.find(item => item.group === group)?.latest ?? null;
              return (
                <Tooltip title={`${group} latest: ${formatMetricValueWithGrade(latest, metric)}`} key={group}>
                  <Chip
                    clickable
                    aria-pressed={active}
                    onClick={() => toggleGroup(group)}
                    label={`${group} ${latest !== null ? `(${formatMetricValueWithGrade(latest, metric)})` : ''}`}
                    variant={active ? 'default' : 'outlined'}
                    style={{
                      maxWidth: 220,
                      background: active ? '#eff6ff' : '#ffffff',
                      color: active ? '#1e3a8a' : '#475569',
                      border: `1px solid ${active ? colorForGroup(group) : '#cbd5e1'}`,
                      fontWeight: active ? 900 : 700,
                      boxShadow: active ? '0 8px 18px rgba(37, 99, 235, 0.12)' : 'none',
                    }}
                    icon={<span style={{ width: 9, height: 9, borderRadius: 99, background: colorForGroup(group), display: 'inline-block', marginLeft: 8 }} />}
                  />
                </Tooltip>
              );
            })
          ) : (
            <Box style={{ width: '100%', padding: '8px 4px' }}>
              <Typography style={{ color: '#64748b', fontSize: 12 }}>
                No series match "{searchQuery.trim()}".
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {!visibleGroups.length ? (
        <Box minHeight={360} display="flex" alignItems="center" justifyContent="center" flexDirection="column" style={{ color: '#64748b', border: '1px dashed #cbd5e1', borderRadius: 18 }}>
          <Typography style={{ fontWeight: 900, marginBottom: 6 }}>No series selected</Typography>
          <Typography variant="body2">Choose at least one chip above to show it on the graph.</Typography>
        </Box>
      ) : (
        <Box style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 18, padding: '16px 16px 8px', height: 380 }}>
          <Line data={chartData} options={chartOptions} />
        </Box>
      )}
    </Box>
  );
};